import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { BodyText } from '@calendly/ui/components/typography'
import { useContext } from 'react'

import { useNotetakerEntitled } from 'features/meeting_intelligence'
import { useNotetakerFreeExperiment } from 'hooks/useNotetakerFreeExperiment'

import { AiBetaBanner } from './ai_beta_banner'
import { NotetakerBanner } from './notetaker_banner'
import { TabContainer } from './styles'
import {
  InviteesWidget,
  LocationsWidget
} from './widgets'
import { MeetingDetailDependenciesContext } from '../meeting_detail_context'

function getLocationForExternalMeeting(meeting, transformLocationKind) {
  const { locationKind, location } = meeting
  if (!locationKind) {
    return null
  }

  // Need to transform the search-api LocationKind enum to a format used in the LocationWidget
  const kind = locationKind.toLowerCase()
  const name = transformLocationKind(locationKind)

  return {
    kind,
    name,
    joinUrl: location,
  }
}

export const ExternalMeetingDetails = ({ meeting, miSettings, user }) => {
  const source = meeting.kind === 'GOOGLE' ? 'Google' : 'Outlook'
  const { isEntitled: isNotetakerEntitled } = useNotetakerEntitled()
  const { isInExperiment: isInNotetakerFreeExperiment } = useNotetakerFreeExperiment()
  const isEligibleMeeting = ['ZOOM', 'GOOGLE_MEET'].includes(meeting?.locationKind)
    || ['zoom_conference', 'google_conference'].includes(meeting?.locationType)
  const isFutureMeeting = !meeting.past

  const meetsBaseBannerCriteria = isFutureMeeting && isEligibleMeeting
  const showNotetakerBanner = meetsBaseBannerCriteria
    && ((isNotetakerEntitled && !!miSettings?.miEnabled) || isInNotetakerFreeExperiment)
  const showBetaBanner = isNotetakerEntitled
    && isFutureMeeting
    && !miSettings?.miEnabled
    && isEligibleMeeting
    && !isInNotetakerFreeExperiment

  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies) {
    throw new Error('ExternalMeetingDetails must be wrapped in a MeetingDetailDependenciesProvider')
  }
  const { transformLocationKind } = dependencies

  const meetingForLocationWidget = {
    location: meeting.location,
    locationKind: meeting.locationKind,
    locationType: meeting.locationType,
    externalLocation: getLocationForExternalMeeting(meeting, transformLocationKind),
    cancelled: meeting.cancelled,
    past: meeting.past,
  }

  return (
    <TabContainer>
      <InviteesWidget event={meeting} />
      <LocationsWidget event={meetingForLocationWidget} />
      <BodyText color="muted">
        {HtmlParser(I18n.t('publisher.meetings.external_source', { externalIntegration: source }))}
      </BodyText>
      {showNotetakerBanner && <NotetakerBanner event={meeting} user={user} isInNotetakerFreeExperiment={isInNotetakerFreeExperiment} />}
      {showBetaBanner && <AiBetaBanner userHasEnabledMiBefore={!!miSettings?.userHasEnabledMiBefore} user={user} />}
    </TabContainer>
  )
}
